import differenceInDays from 'date-fns/differenceInDays'
import isArray from 'lodash/isArray'
import isDate from 'lodash/isDate'
import isNumber from 'lodash/isNumber'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import isUndefined from 'lodash/isUndefined'
import uniqWith from 'lodash/uniqWith'
import omitBy from 'lodash/omitBy'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import differenceWith from 'lodash/differenceWith'
import { parse, stringify } from 'query-string'
import parseDate from '@signal/utils/parseDate'
import {
  ALL_MANAGED_DATE_PARAMETERS,
  ALL_MANAGED_QUERY_PARAMETERS,
  GLOBAL_QUERY_PARAMETERS,
  SUBST_REQUIRED_ANCESTOR_ID,
  SUBST_WIDGET_ID,
  QP_COMPARISON_DATE_START,
  QP_COMPARISON_DATE_END,
  QP_COLUMN_SET,
  QP_DATE_START,
  QP_DATE_END,
  QP_FILTERS,
  QP_MAP_OPTION,
  QP_MAP_DEALER_MARKERS,
  QP_MAP_STATE,
  QP_METRICS,
  QP_SUBST_CHAR,
  QP_MAP_DEALER_MARKERS_TOGGLE,
  QP_MAP_OVERLAY
} from './constants'
import isNull from 'lodash/isNull'

const QP_SUBST_REGEX = new RegExp(`${QP_SUBST_CHAR}.+${QP_SUBST_CHAR}`, 'g')

/**
 * this function converts legacy route filter formats to the new format for compatibility with old bookmarks
 * @param {Array/Object} routeFilters - an array of route filter objects what may be a non-array object (old style)
 * @return {Array} - the modern array format for filters
 */
function ensureRouteFilterIsArray(routeFilters) {
  return isArray(routeFilters)
    ? routeFilters
    : // convert from legacy array format for filters in route
      map(routeFilters, (value, name) => ({ name, value }))
}

/**
 * this function strips substitution codes out of a QP template (e.g. takeover_:accountId::widgetId:: becomes takeover_)
 * @param {String} qpTemplate - the query parameter template
 * @return {String} - tghe QP template w/o substitution codes
 */
export function stripSubstFromQP(qpTemplate) {
  return qpTemplate.replace(QP_SUBST_REGEX, '')
}

/**
 * this function is a wrapper around query-params parse that removes empty query parameters, such as &foo=bar&&baz=quux
 * @param {String} search - the query params to parse
 * @return {Object} - the parse query parameter object
 */
export function parseAndStrip(search) {
  const ret = parse(search)
  delete ret[''] // elmiminate empty qp
  return ret
}

/**
 * validation functions
 */

/**
 * this function validates that a date range is valid (has a end date that comes after a start date
 * @param {Object} - an object with a startDate and endDate, which should both be Javascript Date() objects
 * @return {Boolean} - true if this date range was valid
 */
export function validateDateRange({ startDate, endDate } = {}) {
  return (
    isDate(startDate) &&
    isDate(endDate) &&
    differenceInDays(endDate, startDate) >= 0
  )
}

/**
 * this function validates that the given date range is valid
 * @param {Object} - an object with these members:
 *   {Date} startDate - the start date, as a JS date
 *   {Date} endDate - the end date, as a JS date
 * @return {Boolean} - true if this date range was valid
 */
export function validateDates({ startDate, endDate }) {
  return (
    startDate &&
    endDate &&
    validateDateRange({
      startDate,
      endDate
    })
  )
}

/**
 * this function validates filters
 * @param {Array} filters - an array of route filter objects
 * @return {Boolean} - whether the given filters were valid
 */
export function validateFilters(filters) {
  return (
    isArray(filters) &&
    filters.every(filter => {
      const filterKeys = Object.keys(filter)
      return filterKeys.includes('filter') || filterKeys.includes('name')
    })
  )
}

/**
 * this function validates map states
 * @param {Object} mapState - a map state object
 * @return {Boolean} - whether the given map state was valid
 */
export function validateMapState(mapState) {
  return (
    isObject(mapState) &&
    isObject(mapState.center) &&
    isNumber(mapState.center.lat) &&
    isNumber(mapState.center.lng) &&
    isNumber(mapState.zoom)
  )
}

/**
 * this function validates metrics
 * @param {Array} metrics - an array of metrics
 * @return {Boolean} - whether the given metrics were valid
 */
export function validateMetrics(metrics) {
  return isArray(metrics) && metrics.every(metric => isString(metric))
}

/**
 * this function validates table column sets when they are in their JSON (non-string) form
 * they are also valid as a string which refers to a named column set, but that form is not validated here
 * @param {Array} columnSet - an array of indexes (a column set)
 * @return {Boolean} - whether the given column set was valid JSON
 */
export function validateTableColumnSetJSON(columnSet) {
  return isArray(columnSet) && columnSet.every(index => isNumber(index))
}

/**
 * this function "cooks" a query parameter string value into a non-string type
 * @param {String} qpType - the type of query parameter being requested (one of the QP_ constants)
 * @param {String} value - the string value found for this query parameter
 * @return {Varies} - the resulting cooked value, which varies by query parameter, or undefined if not found
 */
export function cookManagedQPValue(qpType, value) {
  switch (qpType) {
    case QP_DATE_START:
    case QP_DATE_END:
      // dates must be parsed, and if they don't parse, return undefined
      const date = value && parseDate(value)
      // detect missing or failed-to-parse dates
      return !value || isNaN(date.getTime()) ? undefined : date
    case QP_FILTERS:
      try {
        // ensure the filter is an array containing the expected kinds of values, or return undefined
        const filters = ensureRouteFilterIsArray(JSON.parse(value))
        return validateFilters(filters) ? filters : undefined
      } catch (e) {
        return undefined
      }
    case QP_MAP_OPTION:
      const mapOption = parseInt(value, 10)
      return isNaN(mapOption) ? undefined : mapOption
    case QP_MAP_DEALER_MARKERS:
      const markerState = value
      return markerState ? markerState : false
    case QP_MAP_DEALER_MARKERS_TOGGLE:
      const toggleState = value
      return toggleState ? toggleState : false
    case QP_MAP_OVERLAY:
      const mapOverlay = parseInt(value, 10)
      return isNull(mapOverlay) ? null : mapOverlay
    case QP_MAP_STATE:
      try {
        // ensure the map state is the expected object, or return undefined
        const mapState = JSON.parse(value)
        return validateMapState(mapState) ? mapState : undefined
      } catch (e) {
        return undefined
      }
    case QP_METRICS:
      try {
        // ensure the metrics are an array of strings, or return undefined
        const metrics = JSON.parse(value)
        return validateMetrics(metrics) ? metrics : undefined
      } catch (e) {
        return undefined
      }
    case QP_COLUMN_SET:
      try {
        // ensure the metrics are an array of numbers, or return the original string (might be a named column set)
        const columnSet = JSON.parse(value)
        return validateTableColumnSetJSON(columnSet) ? columnSet : value
      } catch (e) {
        return value
      }
    default:
      return value
  }
}

/**
 * this function reduces all managed query parameters
 * @param {Object} an object with these members -
 *   {Object} reducerCallback - the reducer callback.  It will be provided with an object containing the found
 *     key in the query parameters (foundKey), the full set of query parameters being reduced (params) and the
 *     type of query parameter that was found (qpType),  It must return the accumulated params
 *   {Object} queryParams - query params that were parsed by query-string module - this is preferred
 *   {Object} search - the search query string (unparsed) - queryParams is preferred instead of this
 *   {Object} initialReducerValue - the initial value for the reducer
 * @return {Object} - the resulting set of valid managed query parameters, with all values "cooked"
 */
export function reduceManagedQPs({
  callback,
  search,
  queryParams,
  initialReducerValue = {}
}) {
  // providing queryParams is preferred, but we can parse a search string
  if (!queryParams) {
    queryParams = parseAndStrip(search)
  }
  const queryParamKeys = Object.keys(queryParams)

  // reduce all the managed query parameters
  return ALL_MANAGED_QUERY_PARAMETERS.reduce((params, qpType) => {
    const qpMatchRegex = new RegExp(
      `(\\?|^)${qpType.replace(QP_SUBST_REGEX, '.+')}(=|$)`
    )
    // attempt to find query parameter keys which are a match for this managed parameter (ignoring substitution codes)
    const foundKeys = queryParamKeys.filter(keyCur => qpMatchRegex.test(keyCur))
    foundKeys.forEach(foundKey => {
      params = callback({ foundKey, params, qpType, queryParams })
    })
    return params
  }, initialReducerValue)
}

/**
 * this function obtains all valid query parameters managed by this service
 * @param {Object} paramObj - an object with these members -
 *   {Object} queryParams - query params that were parsed by query-string module - this is preferred
 *   {Object} search - the search query string (unparsed) - queryParams is preferred instead of this
 *   {Boolean} includeGlobals - if true, include global query parameters
 *   {Boolean} includeNonGlobals - if true, include non-global query parameters
 * @return {Object} - the resulting set of valid managed query parameters, with all values "cooked"
 */
export function getAllManagedQPs({
  search,
  queryParams,
  excludeGlobals = false,
  excludeNonGlobals = false
}) {
  const managedQPs = reduceManagedQPs({
    callback: ({ foundKey, params, queryParams, qpType }) => {
      const cookedValue =
        foundKey && cookManagedQPValue(qpType, queryParams[foundKey])

      // ensure the value will cook before including it, but keep the uncooked value
      if (!isUndefined(cookedValue)) {
        if (
          GLOBAL_QUERY_PARAMETERS.includes(qpType)
            ? !excludeGlobals
            : !excludeNonGlobals
        ) {
          params[foundKey] = queryParams[foundKey]
        }
      }

      return params
    },
    search,
    queryParams
  })

  // extra date validation
  ALL_MANAGED_DATE_PARAMETERS.forEach(([startDateKey, endDateKey]) => {
    // must have valid start/end date (both must exist, end date must be after start date)
    if (
      !validateDates({
        startDate: cookManagedQPValue(QP_DATE_START, managedQPs[startDateKey]),
        endDate: cookManagedQPValue(QP_DATE_END, managedQPs[endDateKey])
      })
    ) {
      delete managedQPs[startDateKey]
      delete managedQPs[endDateKey]
      // also delete comparison dates if any date fails
      delete managedQPs[QP_COMPARISON_DATE_START]
      delete managedQPs[QP_COMPARISON_DATE_END]
    }
  })

  return managedQPs
}

/**
 * this function strips all query parameters managed by this service (all not unknown) from the given query parameter
 * object (parsed by query-string)
 * @param {Object} queryParams - query params that were parsed by query-string module
 * @return {Object} - the query parameters provided, with all managed parameters stripped
 */
export function stripAllManagedQPs(queryParams) {
  return reduceManagedQPs({
    callback: ({ foundKey, params }) => {
      if (foundKey) {
        delete params[foundKey]
      }
      return params
    },
    queryParams,
    initialReducerValue: { ...queryParams }
  })
}

/**
 * this function builds a query parameter string from the given query params
 * @param {Object} queryParams - query params to stringify
 * @return {String} - a string ready to append to a URL to supply all its query params
 */
export function buildManagedQPString(queryParams) {
  return `?${stringify(queryParams)}`
}

/**
 * this function merges the QP provided in search with QP that may exist on the URL already, and returns the resulting
 * merged URL.  Any QP that exist on the search will be superceded by matching QP that exists in the URL
 * also, QP_FILTERS will be merged (also preferring filters that exist on the URL rather than in search)
 * @param {String} url - a URL to merge QP into
 * @param {String} search - the QP to merge into the URL
 * @return {String} - the URL, with QP from "search" merged in
 */
export function mergeQPIntoUrl(url, search) {
  const urlSearchIndex = url.indexOf('?')
  const urlNoParams =
    urlSearchIndex === -1 ? url : url.substr(0, urlSearchIndex)
  const urlSearch = urlSearchIndex === -1 ? '?' : url.substr(urlSearchIndex)
  const urlParams = parseAndStrip(urlSearch)
  const paramsToMergeIn = parseAndStrip(search)

  // new query parameters will combine both (preferring those from URL)
  const newParams = {
    ...paramsToMergeIn,
    ...urlParams
  }

  // special handling for QP_FILTERS
  // we want to merge QP_FILTERS when found rather than blast QP_FILTERS from urlParams
  const findManagedFilter = ({ foundKey, params, queryParams, qpType }) => {
    if (foundKey && qpType === QP_FILTERS) {
      // Return array of filters (previously would covert to object using reduce method)
      return cookManagedQPValue(qpType, queryParams[foundKey]) || []
    }
    return params
  }

  // Remove any null or undefined filter props to ensure accurate comparison when merging props
  const removeNilValues = filter => {
    return omitBy(filter, isNil)
  }
  // find filters from the URL
  const urlFilters = reduceManagedQPs({
    callback: findManagedFilter,
    search: urlSearch,
    initialReducerValue: []
  })

  // find filters from the current search
  const searchFilters = reduceManagedQPs({
    callback: findManagedFilter,
    search,
    initialReducerValue: []
  })

  // this is removing duplicate filters in searchFilters there is a matching urlFilter
  // this ensures that when deeplinks/Urls are generated the urlFilter option is present
  const searchFiltersDifference = differenceWith(
    map(searchFilters, removeNilValues),
    map(urlFilters, removeNilValues),
    function(searchFilters, urlFilters) {
      return searchFilters.filter === urlFilters.filter
    }
  )

  // merge the filters
  // ensure no duplicate filters are added onto the url using lodash uniq method
  const newFilters = uniqWith(
    [...searchFiltersDifference, ...map(urlFilters, removeNilValues)],
    function(searchFilters, urlFilters) {
      return (
        searchFilters.filter === urlFilters.filter &&
        searchFilters.value === urlFilters.value
      )
    }
  )

  if (newFilters.length) {
    // set them back into the new parameters, if any
    newParams[QP_FILTERS] = JSON.stringify(newFilters)
  }

  const newSearch = stringify(newParams)
  return `${urlNoParams}?${newSearch}`
}

/**
 * this function generates a name for a managed query parameter
 * @param {String} qpType - the type of query parameter being requested (one of the QP_ constants)
 * @param {String} widgetId - the widget id
 * @param {String} requiredAncestorId - the ancestor id which is required for a QP match
 * @return {String} - the name of the managed query parameter
 */
export function makeParamName(qpType, widgetId, requiredAncestorId) {
  return qpType
    .replace(SUBST_WIDGET_ID, widgetId)
    .replace(
      SUBST_REQUIRED_ANCESTOR_ID,
      requiredAncestorId ? `${requiredAncestorId}:` : ''
    )
}

/**
 * this function is a quick and dirty way to replace a managed query parameter, such as if you need to do a
 * window.replace
 * @param {Object} an object with these members -
 *   {Object} queryParams - query params that were parsed by query-string module - this is preferred
 *   {Object} search - the search query string (unparsed) - queryParams is preferred instead of this
 *   {String} qpType - the type of query parameter being requested (one of the QP_ constants)
 *   {Varies} newValue - the new value to apply for the managed query parameter
 *   {String} widgetId - the widget id
 *   {String} requiredAncestorId - the ancestor id which is required for a QP match
 * @return {String} - the name of the managed query parameter
 */
export function replaceManagedQPInSearch({
  queryParams,
  search,
  qpType,
  newValue,
  widgetId,
  requiredAncestorId
}) {
  if (!queryParams) {
    queryParams = parseAndStrip(search)
  }

  const paramName = makeParamName(qpType, widgetId, requiredAncestorId)
  const replacement = newValue
    ? encodeURIComponent(
        JSON.stringify(cookManagedQPValue(qpType, JSON.stringify(newValue)))
      )
    : ''
  queryParams[paramName] = replacement
  return `?${stringify(queryParams)}`
}
